import React, { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ChartNoAxesColumnIcon,
  LockIcon,
  SearchIcon,
  UserSearchIcon,
  XIcon,
} from 'lucide-react'
import {
  Box,
  IconButton,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'

import { Card, Input, Skeleton } from '@repo/ui'
import { EmptyState, TourAdoption, TourSectionNote, TourHeadCell } from 'components'
import { useTourAnalytics } from 'hooks/api'
import type { TourAnalytics } from 'services/tourAnalyticsApi'
import { relativeOrDateTime } from 'utils'
// Direct subpath rather than the `components/onboarding` barrel — that barrel
// re-exports ToursDialog, which reaches back into `components`, and Header
// documents the same cycle. The registry itself is pure data.
import {
  TOURS,
  TOUR_ANCHORS,
  TOUR_OUTRO_STEP,
  tourAnchors,
} from 'components/onboarding/tours'

/**
 * Product-tour analytics (VRG-267) — the Tours tab of /internal/analytics.
 *
 * Three sections, analytics first, mirroring what Vergence built:
 *
 *  1. **Analytics** — a per-step funnel for each tour and a compact cross-tour
 *     summary, over a rolling 30-day window, optionally scoped to one person.
 *  2. **Catalog** — every authored tour and its steps. Read-only: the registry is
 *     CODE (`components/onboarding/tours.ts`), so there is nothing to edit here and
 *     an edit affordance would be a lie.
 *  3. **Anchors** — the `data-tour` contract and which tours each anchor carries,
 *     which is what turns "step 4 has a cliff" into "step 4's anchor moved".
 *
 * WHY THE THREE STAY TOGETHER, despite Anchors being engineering-facing and Labs
 * being where engineering-facing things live: a drop-off is only actionable next to
 * the step copy that caused it and the anchor that step depends on. Split apart,
 * the funnel is a number with nothing to do about it.
 *
 * NOTE — THERE IS NO CROSS-TOUR ROLLUP HERE, on purpose. "Overall completion across
 * all tours" would be the one figure a retired tour could silently distort: its
 * events outlive its catalog entry (the key is free text, by design), so it would
 * keep contributing to a total nobody could see it in. Every figure on this page is
 * per-tour, and a tour with no catalog entry is labelled Retired rather than folded
 * into anything.
 */

/** Rolling window, matching the API. Displayed, not enforced, client-side. */
const WINDOW_LABEL = 'Last 30 days'

// ─── Small shared bits ─────────────────────────────────────────────────────

// ─── Skeleton (§19 — mirrors the loaded layout, region for region) ─────────

const ToursPanelSkeleton = () => (
  <Stack spacing={6}>
    <Card title="Adoption">
      <Stack spacing={3} sx={{ mt: 4 }}>
        <Skeleton height={18} width="40%" />
        <Skeleton height={14} lines={4} />
      </Stack>
    </Card>
    <Card title="Funnels">
      <Stack spacing={3} sx={{ mt: 4 }}>
        <Skeleton height={18} width="30%" />
        <Skeleton height={8} />
        <Skeleton height={8} />
        <Skeleton height={8} />
        <Skeleton height={8} />
      </Stack>
    </Card>
    <Card title="Catalog">
      <Stack spacing={3} sx={{ mt: 4 }}>
        <Skeleton height={14} lines={3} />
      </Stack>
    </Card>
  </Stack>
)

// ─── Panel ─────────────────────────────────────────────────────────────────

export const ToursPanel = () => {
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()

  /**
   * `?user=<email>` is the URL, not component state, so a scoped view is
   * LINKABLE — the point of the filter is to paste "here is what this person has
   * actually studied" into a conversation. The input below is a draft of that
   * param, committed on Enter.
   */
  const scopedUser = searchParams.get('user') ?? ''
  const [draftUser, setDraftUser] = useState(scopedUser)

  const applyUser = (value: string) => {
    const next = new URLSearchParams(searchParams)
    if (value.trim()) next.set('user', value.trim())
    else next.delete('user')
    setSearchParams(next, { replace: true })
  }

  const { data, isLoading, error } = useTourAnalytics(scopedUser || undefined)

  const reachedByTour = useMemo(() => {
    const map = new Map<string, Map<number, number>>()
    for (const row of data?.steps ?? []) {
      const inner = map.get(row.tourKey) ?? new Map<number, number>()
      inner.set(row.stepIndex, row.reached)
      map.set(row.tourKey, inner)
    }
    return map
  }, [data])

  const summaryByTour = useMemo(() => {
    const map = new Map<string, TourAnalytics['summary'][number]>()
    for (const row of data?.summary ?? []) map.set(row.tourKey, row)
    return map
  }, [data])

  /** Keys with events that no longer have a catalog entry. Named, never hidden —
   *  a figure whose subject no longer exists must say so. */
  const retiredKeys = useMemo(
    () => (data?.summary ?? []).filter(row => !TOURS.some(t => t.id === row.tourKey)),
    [data],
  )

  /**
   * Anchor id → what depends on it. Derived from the registry, so it cannot drift
   * from what `check-tour-anchors.mjs` enforces at build time.
   *
   * The OUTRO's anchor is credited explicitly. `tourAnchors()` deliberately omits
   * it (including it would make every tour without a `scopeAnchor` claim to be
   * "on this page" everywhere — see the comment on `TOUR_OUTRO_STEP`), so a naive
   * union labelled `sidebar-help` "declared but unused" while every tour in the
   * app in fact ends on it. Reading that as dead and deleting the anchor would
   * break the closing step of every tour at once.
   */
  const anchorUsage = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const anchor of Object.values(TOUR_ANCHORS)) map.set(anchor, [])
    for (const tour of TOURS) {
      for (const anchor of tourAnchors(tour)) {
        map.set(anchor, [...(map.get(anchor) ?? []), tour.label])
      }
    }
    if (TOUR_OUTRO_STEP.anchor) {
      map.set(TOUR_OUTRO_STEP.anchor, [
        ...(map.get(TOUR_OUTRO_STEP.anchor) ?? []),
        'Every tour (shared closing step)',
      ])
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [])

  if (isLoading) return <ToursPanelSkeleton />

  // A 403 is "you are not allowed to look", which must never render as "nobody
  // took the tour". Distinct state, distinct copy.
  const status = (error as (Error & { status?: number }) | null)?.status
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={<LockIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />}
          title={status === 403 ? 'You cannot view tour analytics' : 'Could not load tour analytics'}
          text={
            status === 403
              ? 'Tour analytics need the "View internal analytics" permission. Ask a platform admin to grant it.'
              : 'The request failed. Reload the page, or check the API is running.'
          }
          sx={{ py: 10 }}
        />
      </Card>
    )
  }

  const unresolvedUser = Boolean(data?.user && !data.user.id)
  const hasEvents = (data?.totalEvents ?? 0) > 0

  const scopeRow = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={4}
      alignItems={{ md: 'center' }}
      justifyContent="space-between"
    >
      <Box sx={{ maxWidth: 340, width: '100%' }}>
        <Input
          placeholder="Scope to one person by email"
          value={draftUser}
          onValueChange={setDraftUser}
          // `handleKeyDown`, not `onKeyDown` — the shared Input's prop name.
          handleKeyDown={event => {
            if (event.key === 'Enter') applyUser(draftUser)
          }}
          onBlur={() => applyUser(draftUser)}
          startAdornment={<SearchIcon size={16} strokeWidth={1} />}
          endAdornment={
            scopedUser ? (
              // X, not a trash can: this clears a filter, it does not destroy a
              // record (§38).
              <IconButton
                size="small"
                aria-label="Clear user scope"
                onClick={() => {
                  setDraftUser('')
                  applyUser('')
                }}
              >
                <XIcon size={14} strokeWidth={1} />
              </IconButton>
            ) : undefined
          }
        />
      </Box>
      <Typography variant="body3" color="text.secondary">
        {WINDOW_LABEL}
        {data?.computedAt ? ` · computed ${relativeOrDateTime(data.computedAt)}` : ''}
        {scopedUser ? ` · scoped to ${scopedUser}` : ''}
      </Typography>
    </Stack>
  )

  return (
    <Stack spacing={6}>
      {scopeRow}

      {/* ── 1. Analytics ─────────────────────────────────────────────────── */}
      {unresolvedUser ? (
        <Card>
          <EmptyState
            icon={
              <UserSearchIcon size={28} strokeWidth={1} color={theme.palette.text.secondary} />
            }
            title={`No user matches ${scopedUser}`}
            text="Nothing is being shown rather than everyone's figures, which would be worse: a funnel under a name that does not exist reads as that person's behaviour."
            sx={{ py: 10 }}
          />
        </Card>
      ) : !hasEvents ? (
        <Card>
          <EmptyState
            icon={
              <ChartNoAxesColumnIcon
                size={28}
                strokeWidth={1}
                color={theme.palette.text.secondary}
              />
            }
            title="No tour events recorded yet"
            text={
              scopedUser
                ? `${scopedUser} has not started any tour in the last 30 days. The catalog below is what they could take.`
                : 'Nobody has started a tour in the last 30 days. This is an empty log, not a zero completion rate — the two look identical on a chart, which is why this says so in words.'
            }
            sx={{ py: 10 }}
          />
        </Card>
      ) : (
        <>
          <TourAdoption
            summary={data?.summary ?? []}
            steps={data?.steps ?? []}
            scopeNote={
              scopedUser ? `Scoped to ${scopedUser}. ` : 'All companies. '
            }
          />
        </>
      )}

      {/* ── 2. Catalog ───────────────────────────────────────────────────── */}
      <Card title="Catalog">
        <Stack spacing={5} sx={{ mt: 4 }}>
          <TourSectionNote>
            Read-only. Tours are typed data in{' '}
            <code>components/onboarding/tours.ts</code>, reviewed in the same change as
            the UI they point at, so authoring one is a code change rather than an edit
            here.
          </TourSectionNote>
          {TOURS.map(tour => (
            <Stack key={tour.id} spacing={2}>
              <Stack direction="row" spacing={3} alignItems="baseline" flexWrap="wrap">
                <Typography variant="body2" fontWeight={600}>
                  {tour.label}
                </Typography>
                <Typography variant="body3" color="text.secondary">
                  {tour.category} · {tour.steps.length} steps · id <code>{tour.id}</code>
                </Typography>
              </Stack>
              <TourSectionNote>{tour.description}</TourSectionNote>
              <TourSectionNote>
                Offered on the page carrying{' '}
                <code>{tour.scopeAnchor ?? 'any of its anchors'}</code>. Deep link:{' '}
                <code>?tour={tour.id}</code>
              </TourSectionNote>
              <Box component="ol" sx={{ m: 0, pl: 6 }}>
                {tour.steps.map(step => (
                  <Typography
                    key={step.id}
                    component="li"
                    variant="body3"
                    color="text.secondary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {step.title}
                    {step.anchor ? ` — ${step.anchor}` : ' — centered, no anchor'}
                  </Typography>
                ))}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Card>

      {/* ── 3. Anchors ───────────────────────────────────────────────────── */}
      <Card title="Anchor contract">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <TourSectionNote>
            Every anchor below must exist as a <code>data-tour</code> attribute on a
            real surface. <code>scripts/check-tour-anchors.mjs</code> runs as the first
            step of the platform build, so removing one is a BUILD failure rather than a
            tour that quietly degrades to a centered card. When a funnel develops a
            cliff at one step, this is where you check whether that step&apos;s anchor
            still exists.
          </TourSectionNote>
          <MuiTable size="small">
            <TableHead>
              <TableRow>
                <TourHeadCell>data-tour</TourHeadCell>
                <TourHeadCell>Tours that depend on it</TourHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {anchorUsage.map(([anchor, tours]) => (
                <TableRow key={anchor}>
                  <TableCell sx={{ fontSize: 13 }}>
                    <code>{anchor}</code>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13, color: 'text.secondary' }}>
                    {tours.length > 0 ? tours.join(', ') : 'None — declared but unused'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </Stack>
      </Card>
    </Stack>
  )
}
